// @ts-ignore
import { PROBLEM_INNER_TYPES, PROBLEM_STATUS_DISPLAY_NAMES } from '@yandex-int/k-common/constants';
import { LESSON_STATUS, StudentProblemResult } from '@yandex-int/k-common/typings';
import b from 'b_';
import { showIntlMessageNotification as showIntlMessageNotificationAction, Text } from 'platform-components';
import { GRAVITIES, NOTIFICATION_THEMES } from 'platform-components/src/components/constants';
import React, { useMemo, useRef } from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { accessibleClick, accessibleMouseHover } from '@edu-frontend/modules/src/shared/lib/a11y';

import { useIsDesktop } from 'common.components/lesson-content/utils';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { LessonStore } from 'common.components/lesson/typings';
import { isLessonCompleted } from 'common.components/lesson/utils';
import { resultsActions } from 'common.components/results/actions';
import { CriteriaStats, PROBLEM_CELL_THEME } from 'common.components/results/typings';
import { getStubValue } from 'common.components/results/utils';
import { getFormattedTime } from 'utils/get-formatted-time';
import { getOpenAnswerAttemptId, getOpenAnswerUrl } from 'utils/open-answer';

import * as selectors from '../selectors';
import { AdditionalResultCellParams } from '../typings';

import { ProblemResultCellOpenAnswer } from './results__problem-result-cell-open-answer';

import './results__problem-result-cell.scss';

interface OwnProps {
    additionalParams?: AdditionalResultCellParams;
    problem: StudentProblemResult;
    problemId: number;
    problemLinkId: number;
    studentId: number;
    cardNumber: number;
    onMouseEnter?: (ref: HTMLDivElement | null) => void;
    onClick?: () => void;
}

interface DispatchProps {
    setShouldShowDescriptionPopup: typeof resultsActions.setShouldShowDescriptionPopup;
    setDescriptionPopupData: typeof resultsActions.setDescriptionPopupData;
    showIntlMessageNotification: typeof showIntlMessageNotificationAction;
}

interface StoreProps {
    lessonStatus: LESSON_STATUS | null;
    shouldShowAttemptsValue: boolean;
    shouldShowTimeValue: boolean;
    shouldShowPointsValue: boolean;
    studentsOnline: Record<number, number | null>;
    isEvaluable: boolean;
    isEvaluated: boolean;
}

interface Props extends OwnProps, WrappedComponentProps, StoreProps, DispatchProps {}

const ProblemResultCell = (props: Props) => {
    const {
        intl,
        lessonStatus,
        problem,
        problemId,
        problemLinkId,
        shouldShowAttemptsValue,
        shouldShowTimeValue,
        shouldShowPointsValue,
        studentId,
        studentsOnline,
        cardNumber,
        onMouseEnter,
        onClick,
        additionalParams = {} as AdditionalResultCellParams,
        isEvaluable,
        isEvaluated,
    } = props;
    const { attempt_number: attemptNumber, status, time, points } = problem;
    const { isOpenAnswer = false, isRobot = false, hasPaste } = additionalParams;

    const isTheory = problem.type === PROBLEM_INNER_TYPES.THEORY;
    const isCompleted = Boolean(lessonStatus) && isLessonCompleted(lessonStatus!);
    const openAnswerUrl = getOpenAnswerUrl(problem);
    const openAnswerAttemptId = getOpenAnswerAttemptId(problem, problemLinkId, studentId);
    const isOpenAnswerEntered = Boolean(openAnswerUrl);

    const cellRef = useRef<HTMLDivElement>(null);

    // eslint-disable-next-line complexity
    const cellTheme = useMemo((): PROBLEM_CELL_THEME => {
        if (hasPaste) {
            return PROBLEM_CELL_THEME.PASTED;
        }

        if (isTheory) {
            return PROBLEM_CELL_THEME.CLEAR;
        }

        if (isOpenAnswer) {
            if (isOpenAnswerEntered) {
                return PROBLEM_CELL_THEME.OPEN_ANSWER;
            }
            return PROBLEM_CELL_THEME.CLEAR;
        }

        if (status === PROBLEM_STATUS_DISPLAY_NAMES.ANSWERED) {
            return PROBLEM_CELL_THEME.ANSWERED;
        }

        if (status === PROBLEM_STATUS_DISPLAY_NAMES.INCORRECT) {
            if ((isRobot && isCompleted) || !isRobot) {
                return PROBLEM_CELL_THEME.INCORRECT;
            }
        }

        if (status === PROBLEM_STATUS_DISPLAY_NAMES.CORRECT) {
            if (shouldShowAttemptsValue && !isRobot) {
                if (attemptNumber === 1) {
                    return PROBLEM_CELL_THEME.ONE_ATTEMPT;
                }

                if (attemptNumber === 3) {
                    return PROBLEM_CELL_THEME.THREE_ATTEMPTS;
                }
            }

            return PROBLEM_CELL_THEME.CORRECT;
        }

        if (isCompleted) {
            return PROBLEM_CELL_THEME.COMPLETED;
        }

        return PROBLEM_CELL_THEME.CLEAR;
    }, [
        attemptNumber,
        isCompleted,
        isTheory,
        isOpenAnswer,
        isOpenAnswerEntered,
        shouldShowAttemptsValue,
        isRobot,
        hasPaste,
        status,
    ]);

    const attemptsValue = useMemo((): string | number => {
        if (!attemptNumber && !time) {
            if (isCompleted) {
                return getStubValue(intl);
            }

            return '';
        }

        return attemptNumber;
    }, [attemptNumber, intl, isCompleted, time]);

    const pointsValue = useMemo((): string | React.ReactElement => {
        if (!attemptNumber && !time) {
            if (isCompleted) {
                return (
                    <Text size={Text.SIZE.S} weight={Text.WEIGHT.M}>
                        {getStubValue(intl)}
                    </Text>
                );
            }

            return '';
        }

        return (
            <Text id="lessonView.results.cell.points" size={Text.SIZE.S} values={{ points }} weight={Text.WEIGHT.M} />
        );
    }, [points, intl, isCompleted, attemptNumber, time]);

    const timeValue = useMemo((): string => {
        if (!attemptNumber && !time) {
            if ((isCompleted && !shouldShowAttemptsValue) || isTheory) {
                return getStubValue(intl);
            }

            return '';
        }

        if (time === 0) {
            return getStubValue(intl);
        }

        return getFormattedTime(time, 'm:ss');
    }, [attemptNumber, isTheory, intl, isCompleted, shouldShowAttemptsValue, time]);

    const criteriaStatsRef = useRef<CriteriaStats>();

    // eslint-disable-next-line complexity
    const cellContent = useMemo((): React.ReactNode => {
        if (isTheory) {
            const isSeen = Boolean(time);

            return (
                <>
                    <div className={b('results', 'problem-result-theory', { seen: isSeen })} />
                    {shouldShowTimeValue && <Text size={Text.SIZE.S}>{timeValue}</Text>}
                </>
            );
        }

        if (isOpenAnswer) {
            return (
                <ProblemResultCellOpenAnswer
                    attemptId={openAnswerAttemptId}
                    criteriaStatsRef={criteriaStatsRef}
                    isOpenAnswerEntered={isOpenAnswerEntered}
                    problemId={problemId}
                    shouldShowTimeValue={shouldShowTimeValue}
                    timeValue={timeValue}
                />
            );
        }

        return (
            <>
                {shouldShowAttemptsValue && (
                    <Text size={Text.SIZE.S} weight={Text.WEIGHT.M}>
                        {attemptsValue}
                    </Text>
                )}
                {shouldShowPointsValue && (
                    <Text size={Text.SIZE.S} weight={Text.WEIGHT.M}>
                        {pointsValue}
                    </Text>
                )}
                {shouldShowTimeValue && <Text size={Text.SIZE.S}>{timeValue}</Text>}
            </>
        );
    }, [
        attemptsValue,
        pointsValue,
        isOpenAnswer,
        isOpenAnswerEntered,
        isTheory,
        shouldShowAttemptsValue,
        shouldShowTimeValue,
        shouldShowPointsValue,
        time,
        timeValue,
        openAnswerAttemptId,
        problemId,
    ]);

    const { setDescriptionPopupData, setShouldShowDescriptionPopup } = props;

    const handleMouseEnter = () => {
        setDescriptionPopupData({
            studentId,
            problemId: problemLinkId,
            problemNumber: cardNumber,
            displayParams: {
                isOpenAnswer,
                isRobot,
                hasPaste,
                criteriaStats: criteriaStatsRef.current,
            },
        });

        setShouldShowDescriptionPopup(true);
        if (onMouseEnter) {
            onMouseEnter(cellRef.current);
        }
    };

    const handleMobileClick = () => {
        if (isOpenAnswer && isOpenAnswerEntered) {
            const { showIntlMessageNotification } = props;
            showIntlMessageNotification({
                gravity: GRAVITIES.BOTTOM,
                messageId: 'lessonResults.goToOpenAnswerLink',
                messageValues: {
                    a: (chunks: string) => (
                        <a className="results__notification-link" href={openAnswerUrl!} target="_blank">
                            {chunks}
                        </a>
                    ),
                },
                withExplicitClose: true,
                theme: NOTIFICATION_THEMES.TOAST,
                textType: Text.TYPE.HTML,
            });
        }
    };

    const isDesktop = useIsDesktop();
    const handleClick = isDesktop ? onClick : handleMobileClick;

    const disabled = isEvaluable && !isEvaluated;
    const className = b('results', 'problem-result-cell', {
        online: studentsOnline[studentId] === problemLinkId,
        theme: cellTheme,
        'time-only': !shouldShowAttemptsValue && !shouldShowPointsValue && shouldShowTimeValue,
        disabled,
        clickable: onClick && !disabled,
    });

    return (
        <div
            className={className}
            ref={cellRef}
            {...accessibleClick(!disabled ? handleClick : undefined)}
            {...accessibleMouseHover({ onMouseEnter: handleMouseEnter, describedby: false })}
        >
            <div className="results__problem-result-info">{cellContent}</div>
        </div>
    );
};

const mapStateToProps = <T extends LessonStore>(storeState: T): StoreProps => {
    return {
        lessonStatus: lessonSelectors.getLessonStatus(storeState),
        shouldShowAttemptsValue: selectors.getShouldShowAttemptsValue(storeState),
        shouldShowTimeValue: selectors.getShouldShowTimeValue(storeState),
        shouldShowPointsValue: selectors.getShouldShowPointsValue(storeState),
        studentsOnline: selectors.getStudentsOnline(storeState),
        isEvaluable: lessonSelectors.getIsLessonEvaluable(storeState),
        isEvaluated: lessonSelectors.getIsLessonEvaluated(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            setShouldShowDescriptionPopup: resultsActions.setShouldShowDescriptionPopup,
            setDescriptionPopupData: resultsActions.setDescriptionPopupData,
            showIntlMessageNotification: showIntlMessageNotificationAction,
        },
        dispatch
    );
};

const wrapped = injectIntl(
    connect(mapStateToProps, mapDispatchToProps)(ProblemResultCell)
) as React.ComponentType<OwnProps>;

wrapped.displayName = 'ProblemResultCell';

export default wrapped;
